import React, {useState, useEffect} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import md5 from 'md5';
import { Button, Grid, Badge, Box, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

import { answerPlayQuizQuestion } from '../../../../actions/playQuiz';
import { COLOR_PRIMARY, COLOR_SUCCESS, COLOR_ERROR } from '../../../../constants/styleValues';


const MCAnswers = () => {
    //++++++++++++++++VARIABLES++++++++++++++++
    const startTime = new Date().getTime(); //get the time on render to calculate the needed time for this question
    const dispatch = useDispatch();

    const componentContent = {
        language_DE:{
            sourceText:'Details zur Antwort findest du hier',
            answerSubmitted:'Antwort abgeschickt'
        },
        language_EN:{
            sourceText:'You find answer details here',
            answerSubmitted:'Answer submitted'
        },
        language_FR:{
            sourceText:'Détails de la réponse ici',
            answerSubmitted:'Réponse envoyée'
        }
    }

    //++++++++++++++++STORE SELECTOR++++++++++++++++
    const playerId = useSelector((state) => state.Cookie.playerId); //use the current cookie to filter the results of each player
    const currentQuestion = useSelector((state) => state.Question); //get the current question to get the image infos
    const playQuiz = useSelector((state) => state.PlayQuiz);
    const selectedLanguage = useSelector((state) => state.Language); //get the selected language from the store. set within navigation component
    
    const playQuizNavQuestion = playQuiz.navigation.currentQuestion;
    const playQuizCurrentPlayer = playQuiz.players.filter(player => player.playerId === md5(playerId) )[0]; //get the current player of the playQuiz (e.g. points)
    const playQuizCurrentQuestion = playQuiz.questions[playQuizNavQuestion]; //get the current question ID

    //++++++++++++++++STATE SETTINGS++++++++++++++++++
    const [buttonsAreDisabled, setbuttonsAreDisabled] = useState(playQuizCurrentPlayer.playerHasAnswered) //set the state of the answer buttons after answer is given. If the player has answered, the answer is disabled
    const [playersAnswerId, setPlayersAnswerId] = useState(''); //set the given answer of the player

    //++++++++++++++++USE EFFECT EVENTS++++++++++++++++
    //disable the buttons when the player has answered
    useEffect(() => {
        setbuttonsAreDisabled(playQuizCurrentPlayer.playerHasAnswered);
        setbuttonsAreDisabled(!playQuiz.currentRound.currentRoundIsActive);
    },[playQuizCurrentPlayer.playerHasAnswered,playQuiz.currentRound.currentRoundIsActive])

    //+++++++++++++++++FUCNTIONS++++++++++++++++++++
    //handle the submit event. A player selects an answer
    //the time is calculated and the answer is sent to the server
    //the answer type is also set to give the correct points for this answer (every answertype is different)
    const handleSubmitAnswer = (givenAnswerId) => {
        const endTime = new Date().getTime();
        const neededTime = endTime - startTime;
        dispatch(answerPlayQuizQuestion(playQuizCurrentQuestion.questionId, currentQuestion.settings.answerType, givenAnswerId, neededTime, currentQuestion.timerSec, playerId, playQuiz.quizId,selectedLanguage,playQuizCurrentPlayer.playerAnswerStrike)); //player Id is set within socket
        setbuttonsAreDisabled(true);
        setPlayersAnswerId(givenAnswerId);
    }

    return(
        <Grid container justifyContent="center" alignItems="center" textAlign="center" >
            <Grid container maxWidth="lg" spacing={2} p={2} alignItems="stretch" textAlign="center" justifyContent="center">
                {!currentQuestion ? "" :
                    currentQuestion[selectedLanguage].answers.map((answer, answerId) => {
                        const roundEnded = !playQuiz.currentRound.currentRoundIsActive;
                        // eslint-disable-next-line eqeqeq
                        const isCorrectAnswer = roundEnded && playQuiz.currentRound.correctAnswer == answerId;
                        const isSelected = answerId === playersAnswerId;
                        const isWrongSelected = roundEnded && isSelected && !isCorrectAnswer;

                        //neutral panel look by default, only correct/wrong/selected states get a distinct color - never the default MUI primary (pink) fill
                        let answerSx = {
                            backgroundColor: 'rgba(255,255,255,0.06)',
                            border: '1px solid rgba(255,255,255,0.14)',
                            color: 'text.primary',
                            opacity: 1,
                            boxShadow: 'none',
                            transform: 'none'
                        };
                        if(roundEnded){
                            if(isCorrectAnswer){
                                //the correct answer needs to win the eye immediately: full color, a glow ring and a slight scale-up
                                answerSx = {
                                    backgroundColor: COLOR_SUCCESS,
                                    border: '2px solid #2FB77E',
                                    color: '#0B2818',
                                    opacity: 1,
                                    boxShadow: '0 0 0 4px rgba(61,220,151,0.25)',
                                    transform: 'scale(1.02)'
                                };
                            } else if(isWrongSelected){
                                answerSx = {
                                    backgroundColor: COLOR_ERROR,
                                    border: '2px solid #D93F3F',
                                    color: '#3A0A0A',
                                    opacity: 1,
                                    boxShadow: 'none',
                                    transform: 'none'
                                };
                            } else {
                                //everything that's neither correct nor the player's wrong pick fades back, so the correct answer stands out by contrast
                                answerSx = {
                                    backgroundColor: 'rgba(255,255,255,0.04)',
                                    border: '1px solid rgba(255,255,255,0.08)',
                                    color: 'text.secondary',
                                    opacity: 0.45,
                                    boxShadow: 'none',
                                    transform: 'none'
                                };
                            }
                        } else if(isSelected){
                            answerSx = {
                                backgroundColor: 'rgba(233,61,130,0.16)',
                                border: `2px solid ${COLOR_PRIMARY}`,
                                color: 'text.primary',
                                opacity: 1,
                                boxShadow: 'none',
                                transform: 'none'
                            };
                        }

                        return (
                        <Grid item xs={12} md={6} key={answerId} sx={{display: 'flex'}}>
                            <Badge // eslint-disable-next-line
                                badgeContent={roundEnded ? playQuiz.currentRound.answers.filter(answer => answer == answerId).length : 0}
                                color='primary'
                                sx={{width: '100%', display: 'flex'}}
                            >
                                <Button
                                    fullWidth
                                    variant='contained'
                                    size="large"
                                    key={answerId}
                                    disabled = {buttonsAreDisabled}
                                    startIcon={isCorrectAnswer ? <CheckCircleIcon /> : isWrongSelected ? <CancelIcon /> : null}
                                    sx={{
                                        ...answerSx,
                                        minHeight: '72px',
                                        flex: 1,
                                        transition: 'all 0.3s ease',
                                        '&:hover': { backgroundColor: answerSx.backgroundColor },
                                        '&.Mui-disabled': { ...answerSx }
                                    }}
                                    onClick={(e) => {handleSubmitAnswer(answerId)}}
                                >
                                    {answer}
                                </Button>
                            </Badge>
                        </Grid>
                        )
                    })
                }
                {playersAnswerId !== '' && playQuiz.currentRound.currentRoundIsActive ?
                    <Grid item xs={12}>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            backgroundColor: 'rgba(61,220,151,0.12)',
                            border: `1px solid ${COLOR_SUCCESS}`,
                            borderRadius: '12px',
                            padding: '8px 14px',
                            mt: 1
                        }}>
                            <CheckCircleIcon sx={{color: COLOR_SUCCESS, fontSize: '20px'}} />
                            <Typography sx={{color: COLOR_SUCCESS, fontWeight: 600, fontSize: '14px'}}>
                                {componentContent[selectedLanguage].answerSubmitted}
                            </Typography>
                        </Box>
                    </Grid>
                : null}
                <div>
                    {!playQuiz.currentRound.currentRoundIsActive ?
                        <Button color='secondary' size='large' variant='text' href={currentQuestion.source} target="_blank">
                            {componentContent[selectedLanguage].sourceText}
                        </Button>
                    : ""}
                </div>
            </Grid>
        </Grid>
    )

}

export default MCAnswers